<template>
  <div class="warnings-center">
    <toolbar
      backRoute="/dashboard"
      :beforeBack="refreshNotificationsCounter"
      title="Notifications"
      actionButtonLabel="Refresh"
      actionButtonIcon="autorenew"
      :actionButtonCallback="debouncedLoadFilteredData" />
    <d-row class="mt-5 mb-5 px-0">
      <d-col md="12" class="text-center">
        <d-checkbox
          key="checkbox-show-new"
          inline
          v-model="showNew"
          toggle
          toggle-small>
          Show only new notifications
        </d-checkbox>

        <d-checkbox
          key="checkbox-show-archived"
          inline
          v-model="showArchived"
          toggle
          toggle-small>
          Show archived notifications
        </d-checkbox>
      </d-col>
    </d-row>

    <d-row
      :class="{
        'mt-5 mb-5 px-0': true,
        'is-loading': !allDataLoaded
      }">
      <d-col md="12">
        <d-card
          v-for="(notification, index) of notifications"
          :key="'notification-' + index"
          class="px-3 py-2 mb-3"
          :style="notification.archived_at ? 'background: #f0f0f0;' : null">
          <d-row>
            <d-col md="2" class="d-flex align-middle">
              <d-badge
                :theme="(notification.read_at || notification.archived_at) ? 'secondary' : 'danger'"
                :style="notification.archived_at ? 'opacity: .5;' : null"
                class="align-items-center px-4 py-2 w-100">
                <template v-if="notification.created_at">
                  {{ notification.created_at.split(' ')[0] }}
                  <div class="py-1"></div>
                  {{ notification.created_at.split(' ')[1] }}
                </template>
              </d-badge>
            </d-col>
            <d-col md="6" class="align-middle">
              <strong>
                {{ notification.title }}
              </strong>
              <br>
              <small>
                {{ notification.content }}
              </small>
            </d-col>

            <d-col md="4" class="d-flex justify-content-end">
              <d-button
                @click.prevent="viewItem(notification)"
                theme="success"
                :outline="true">
                <i class="material-icons">visibility</i> View
              </d-button>
              <d-button
                theme="danger"
                @click.prevent="archiveItem(notification.id)"
                :outline="true"
                class="ml-2 spinner-wrapper"
                :disabled="archiveInProgress === notification.id || !!notification.archived_at">
                <span
                  v-if="archiveInProgress === notification.id"
                  class="spinner"></span>
                <template v-if="archiveInProgress !== notification.id">
                  <i class="material-icons">delete</i> Clear
                </template>
              </d-button>
            </d-col>
          </d-row>
        </d-card>

        <d-alert
          :show="initialLoad && showNew && !showArchived && !notifications.length"
          theme="warning">
          There are no new notifications to read
        </d-alert>

        <d-alert
          :show="initialLoad && !showNew && !notifications.length"
          theme="warning">
          There are no notifications
        </d-alert>
      </d-col>
    </d-row>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';

export default {
  name: 'notifications',
  computed: {
    allDataLoaded () {
      if (this.showArchived) {
        return this.loadedCurrent && this.loadedArchived;
      }

      return this.loadedCurrent;
    },
    notifications () {
      return this.notificationsCurrent.concat(this.notificationsArchived).filter(item => {
        if (!this.showArchived && item.archived_at) {
          return false;
        }

        if (this.showNew && item.read_at) {
          return false;
        }

        return true;
      }).sort((itemA, itemB) => {
        let dateA = new Date(itemA.created_at);
        let dateB = new Date(itemB.created_at);
        return dateB - dateA;
      });
    }
  },
  watch: {
    showNew (newValue) {
      if (newValue) {
        this.showArchived = false;
      }
    },
    showArchived (newValue, oldValue) {
      if (newValue) {
        this.showNew = false;
      }

      if (newValue !== oldValue) {
        this.debouncedLoadFilteredData();
      }
    },
    allDataLoaded (newValue) {
      if (newValue) {
        this.initialLoad = true;
      }
    }
  },
  data () {
    return {
      initialLoad: false,
      showArchived: false,
      showNew: true,
      loaded: false,
      notificationsCurrent: [],
      notificationsArchived: [],
      loadError: false,
      loadedCurrent: false,
      loadedArchived: false,
      archiveInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('notifications-list-reload', this.debouncedLoadFilteredData);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadedCurrent = false;
      this.loadedArchived = false;
      this.loadFilteredData();
    }, 500),
    loadFilteredData () {
      ListUtils.loadItemsData(this, '/notifications/items', 'notificationsCurrent', {
        pagination: false,
        where: {
          is_archived: 0
        },
        successAction: (response) => {
          if (response.data) {
            this.loadedCurrent = true;
          }
        }
      });

      if (this.showArchived) {
        ListUtils.loadItemsData(this, '/notifications/items', 'notificationsArchived', {
          pagination: false,
          where: {
            is_archived: 1
          },
          successAction: (response) => {
            if (response.data) {
              this.loadedArchived = true;
            }
          }
        });
      }
    },
    viewItem (notification) {
      if (!notification.read_at) {
        this.readItem(notification.id);
      }

      if (notification.source_type === 'GENEVA_SLOT') {
        this.$bus.$emit('show-monitoring-popup', 'view', notification.source_id);
      }
    },
    readItem (id) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/read/' + parseInt(id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during marking notification as read. Please try again.',
        successAction: () => {
          this.notificationsCurrent = this.notificationsCurrent.map(item => {
            if (item.id === id) {
              let currentTime = new Date();
              let isoString = currentTime.toISOString();
              let readedAt = isoString.replace('T', ' ').substring(0, 19);
              item.read_at = readedAt;
            }

            return item;
          });
        }
      });
    },
    archiveItem (id) {
      this.archiveInProgress = id;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/archive/' + parseInt(id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during marking notification as archived. Please try again.',
        successAction: () => {
          this.debouncedLoadFilteredData();
          this.archiveInProgress = false;
        }
      });
    },
    refreshNotificationsCounter (successCallback) {
      this.$bus.$emit('topbar-get-notifications');
      successCallback();
    }
  },
  beforeDestroy () {
    this.$bus.$off('notifications-list-reload', this.debouncedLoadFilteredData);
  }
};
</script>

<style lang="scss" scoped>
.is-loading {
  opacity: .5;
}

.spinner {
  animation: spinner 1s linear infinite;
  border: 3px solid #c4183c;
  border-left-color: #fff;
  border-radius: 50%;
  display: block;
  height: 24px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%) rotate(0deg);
  width: 24px;

  &-wrapper {
    position: relative;
  }
}

@keyframes spinner {
  from {
    transform: translateX(-50%) translateY(-50%) rotate(0deg);
  }

  to {
    transform: translateX(-50%) translateY(-50%) rotate(360deg);
  }
}
</style>
